import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiFileText, FiAlertCircle, FiTrash2, FiSearch, FiChevronLeft,
  FiUser, FiChevronRight, FiChevronDown, FiArrowLeft, FiArrowRight, FiCheckCircle,
  FiThumbsUp, FiThumbsDown,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../app/routes';
import { useToast } from '../../context/ToastContext';
import '../commissionscan/CommissionScan.css';
import '../../styles/QuoteProposal.css';
import '../homeowners/homeowners.css';
import '../../styles/AcordSmartScanUpload.css';
import {
  getToolboxToken, getNcToken,
  DOCUMENT_TYPE_OPTIONS, isDecOrQuote,
  BILLING_TYPE_DATA, findBillingTypeByValue,
  toDateInput, US_STATES,
  type NowCertsClient, type InsuredData, type CoverageLimitDetails,
  type PropertyItem, type VehicleItem, type DriverItem, type FeeItem, type SelectedOption,
} from './acordSmartScanShared';
import { SortablePaginatedTable, CoverageLimitCard, getOptionStyle } from './AcordSmartScanReviewParts';
import { submitAcordSmartScanToNowCerts } from './acordSmartScanSubmit';
import { useTheme } from '../../context/ThemeContext';

/* ── AMS file browser (adapted from InvoiceScanUpload's inline AmsFileBrowser) ── */
interface AmsFileItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  description?: string;
  size?: string;
  uploader?: string;
  created?: string;
  children?: AmsFileItem[];
}
interface AmsFileSelection { file: AmsFileItem; insuredId: string; folderId: string; }

const AmsFileBrowser: React.FC<{
  insuredId: string;
  clientName: string;
  onFileSelected: (sel: AmsFileSelection | null) => void;
}> = ({ insuredId, clientName, onFileSelected }) => {
  const [rootFolder, setRootFolder] = useState<AmsFileItem | null>(null);
  const [currentFolder, setCurrentFolder] = useState<AmsFileItem | null>(null);
  const [tableData, setTableData] = useState<AmsFileItem[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  const [selectedFile, setSelectedFile] = useState<AmsFileItem | null>(null);
  const [folderHistory, setFolderHistory] = useState<AmsFileItem[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const mapFiles = (rawFiles: any[]): AmsFileItem[] =>
    (rawFiles || []).map((f: any) => ({
      id: f.databaseId,
      name: f.name,
      type: f.fileOrFolder === 'Folder' ? 'folder' : 'file',
      description: f.fileOrFolder,
      size: f.size?.toString() || '-',
      uploader: f.creatorName || '-',
      created: f.createDate || '-',
      children: f.fileOrFolder === 'Folder' ? [] : undefined,
    }));

  const fetchFiles = useCallback(async (folderId?: string) => {
    const url = `${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/getInsuredFilesList?insuredId=${insuredId}`
      + `&nctoken=${getNcToken()}`
      + `&folderId=${folderId || ''}`;
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${getToolboxToken()}` } });
    return res.data;
  }, [insuredId]);

  useEffect(() => {
    setFetchError('');
    setTableLoading(true);
    fetchFiles()
      .then((data) => {
        const fd = data?.data || data;
        const root: AmsFileItem = {
          id: fd.currentFolderId || insuredId,
          name: fd.name || clientName || 'Root',
          type: 'folder',
          children: mapFiles(fd.files || []),
        };
        setRootFolder(root);
        setCurrentFolder(root);
        setTableData(root.children || []);
        setExpandedFolders({ [root.id]: true });
        setFolderHistory([root]);
        setHistoryIndex(0);
      })
      .catch(() => setFetchError('Unable to load files. Please try again.'))
      .finally(() => setTableLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuredId]);

  const openFolder = useCallback((folder: AmsFileItem, childrenOverride?: AmsFileItem[]) => {
    const children = childrenOverride ?? folder.children ?? [];
    const node: AmsFileItem = { ...folder, children };
    setCurrentFolder(node);
    setTableData(children);
    setExpandedFolders((p) => ({ ...p, [folder.id]: true }));
    setFolderHistory((prev) => {
      const base = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      const exists = base.findIndex((f) => f.id === folder.id);
      if (exists !== -1) { setHistoryIndex(exists); return base; }
      const next = [...base, node];
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, [historyIndex]);

  const updateChildrenInTree = (root: AmsFileItem, targetId: string, newChildren: AmsFileItem[]): AmsFileItem => {
    if (root.id === targetId) return { ...root, children: newChildren };
    if (root.children) return { ...root, children: root.children.map((c) => updateChildrenInTree(c, targetId, newChildren)) };
    return root;
  };

  const toggleFolder = useCallback((item: AmsFileItem) => {
    if (expandedFolders[item.id]) {
      setExpandedFolders((p) => ({ ...p, [item.id]: false }));
      return;
    }
    if (item.children && item.children.length === 0) {
      setTableLoading(true);
      fetchFiles(item.id)
        .then((data) => {
          const newChildren = mapFiles(data?.data?.files || []);
          setRootFolder((prev) => (prev ? updateChildrenInTree(prev, item.id, newChildren) : prev));
          openFolder(item, newChildren);
        })
        .catch(() => {})
        .finally(() => setTableLoading(false));
    } else if (item.type === 'folder') {
      openFolder(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedFolders, fetchFiles, openFolder]);

  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex >= 0 && historyIndex < folderHistory.length - 1;

  const handleBack = () => {
    const newIdx = historyIndex - 1;
    const f = folderHistory[newIdx];
    if (!f) return;
    setHistoryIndex(newIdx); setCurrentFolder(f); setTableData(f.children || []);
    setExpandedFolders((p) => ({ ...p, [f.id]: true }));
  };
  const handleForward = () => {
    const newIdx = historyIndex + 1;
    const f = folderHistory[newIdx];
    if (!f) return;
    setHistoryIndex(newIdx); setCurrentFolder(f); setTableData(f.children || []);
    setExpandedFolders((p) => ({ ...p, [f.id]: true }));
  };

  const buildPath = (root: AmsFileItem, targetId: string): AmsFileItem[] => {
    const path: AmsFileItem[] = [];
    const dfs = (node: AmsFileItem, acc: AmsFileItem[]): boolean => {
      const next = [...acc, node];
      if (node.id === targetId) { path.push(...next); return true; }
      for (const c of node.children || []) if (dfs(c, next)) return true;
      return false;
    };
    dfs(root, []);
    return path.length ? path : [root];
  };

  const fmtSize = (size?: string) => {
    if (!size || size === '-') return '—';
    const n = parseInt(size, 10);
    if (isNaN(n)) return size;
    return n >= 1048576 ? (n / 1048576).toFixed(2) + ' MB' : (n / 1024).toFixed(2) + ' KB';
  };

  const renderTreeItem = (item: AmsFileItem, level = 0): React.ReactNode => {
    const expanded = expandedFolders[item.id];
    return (
      <div key={item.id}>
        <div
          className={`cs-ams-tree-row${level === 0 ? ' cs-ams-tree-row--root' : ''}`}
          style={{ paddingLeft: level * 16 + 10 }}
          onClick={() => toggleFolder(item)}
        >
          {expanded ? <FiChevronDown size={11} className="cs-ams-chevron" /> : <FiChevronRight size={11} className="cs-ams-chevron" />}
          <span className="cs-ams-tree-name" title={item.name}>{item.name}</span>
        </div>
        {expanded && (item.children || []).filter((c) => c.type === 'folder').map((c) => renderTreeItem(c, level + 1))}
      </div>
    );
  };

  if (tableLoading && !rootFolder) {
    return (
      <div className="cs-ams-state acord-smartscan-upload__ams-state-pad">
        <span className="cs-ams-spinner" />
        <span>Loading files…</span>
      </div>
    );
  }
  if (fetchError) return <div className="cs-ams-error-msg">{fetchError}</div>;

  return (
    <div className="cs-client-panel cs-client-panel--browser">
      <div className="cs-ams-wrap">
        <div className="cs-ams-client-bar">
          <FiUser size={13} className="cs-ams-client-bar__icon" />
          <span className="cs-ams-client-bar__name">{clientName}</span>
        </div>
        {rootFolder && (
          <>
            {selectedFile && (
              <div className="cs-ams-file-card">
                <div className="cs-ams-file-card__left">
                  <FiFileText size={16} className="cs-ams-file-card__icon" />
                  <div>
                    <p className="cs-ams-file-card__name" title={selectedFile.name}>{selectedFile.name}</p>
                    {selectedFile.size && selectedFile.size !== '-' && (
                      <p className="cs-ams-file-card__size">{fmtSize(selectedFile.size)}</p>
                    )}
                  </div>
                </div>
                <button className="cs-ams-file-card__clear" onClick={() => { setSelectedFile(null); onFileSelected(null); }}>
                  ✕
                </button>
              </div>
            )}
            <div className="cs-ams-breadcrumb">
              <button className="cs-ams-nav-btn" disabled={!canGoBack} onClick={handleBack} title="Back"><FiArrowLeft size={13} /></button>
              <button className="cs-ams-nav-btn" disabled={!canGoForward} onClick={handleForward} title="Forward"><FiArrowRight size={13} /></button>
              <div className="cs-ams-breadcrumb__path">
                {buildPath(rootFolder, currentFolder?.id || rootFolder.id).map((seg, i) => (
                  <React.Fragment key={seg.id}>
                    {i > 0 && <span className="cs-ams-breadcrumb__sep">/</span>}
                    <span className="cs-ams-breadcrumb__seg" title={seg.name}>{seg.name}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="cs-ams-layout">
              <div className="cs-ams-tree">{renderTreeItem(rootFolder)}</div>
              <div className="cs-ams-table-wrap">
                {tableLoading ? (
                  <div className="cs-ams-state"><span className="cs-ams-spinner" /><span>Loading…</span></div>
                ) : tableData.length === 0 ? (
                  <div className="cs-ams-state cs-ams-state--empty">No files in this folder.</div>
                ) : (
                  <table className="cs-ams-table">
                    <thead>
                      <tr>
                        <th className="cs-ams-th">Name</th>
                        <th className="cs-ams-th">Type</th>
                        <th className="cs-ams-th">Size</th>
                        <th className="cs-ams-th">Uploader</th>
                        <th className="cs-ams-th">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((item) => {
                        const isSelected = selectedFile?.id === item.id;
                        return (
                          <tr
                            key={item.id}
                            className={`cs-ams-tr${item.type === 'folder' ? ' cs-ams-tr--folder' : ' cs-ams-tr--file'}${isSelected ? ' cs-ams-tr--active' : ''}`}
                            onClick={() => {
                              if (item.type === 'folder') { toggleFolder(item); return; }
                              setSelectedFile(item);
                              onFileSelected({ file: item, insuredId, folderId: currentFolder?.id || '' });
                            }}
                          >
                            <td className="cs-ams-td cs-ams-td--name"><span title={item.name}>{item.name}</span></td>
                            <td className="cs-ams-td">{item.description || '—'}</td>
                            <td className="cs-ams-td">{fmtSize(item.size)}</td>
                            <td className="cs-ams-td">{item.uploader || '—'}</td>
                            <td className="cs-ams-td">{item.created || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Small client-search box (Prospect / Insured / Carrier all reuse this shape) ── */
function useClientSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NowCertsClient[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/customerlist`, {
        params: { searchText: query, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      })
        .then((r) => setResults(r.data || []))
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [query]);
  return { query, setQuery, results, setResults, loading };
}

/* ── Radio option (mutually-exclusive choices: Prospect/Insured, Create New/Use Existing) ── */
const RadioOption: React.FC<{ checked: boolean; onClick: () => void; label: string }> = ({ checked, onClick, label }) => (
  <label
    onClick={onClick}
    className="acord-smartscan-upload__radio-label"
    style={{ color: checked ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: checked ? 600 : 400 }}
  >
    <span
      className="acord-smartscan-upload__radio-dot"
      style={{ border: `2px solid ${checked ? '#7c3aed' : 'var(--border-glass)'}` }}
    >
      {checked && <span className="acord-smartscan-upload__radio-dot-inner" />}
    </span>
    {label}
  </label>
);

/* ── Toggle switch row (boolean choices: Create New / Use Existing / Create Quote) —
 * full-width card with label on the left and the switch on the right. ── */
const ToggleSwitch: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({ checked, onChange, label }) => (
  <div
    onClick={() => onChange(!checked)}
    className="acord-smartscan-upload__toggle-row"
  >
    {label}
    <span
      className="acord-smartscan-upload__toggle-track"
      style={{ background: checked ? '#7c3aed' : 'var(--border-glass)' }}
    >
      <span
        className="acord-smartscan-upload__toggle-thumb"
        style={{ left: checked ? 20 : 2 }}
      />
    </span>
  </div>
);

interface Props { onGoToHistory?: () => void; }

const AcordSmartScanUpload: React.FC<Props> = ({ onGoToHistory: _onGoToHistory }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showSuccess } = useToast();
  type Step = 'intake' | 'upload' | 'review';
  const [step, setStep] = useState<Step>('intake');

  /* ── Intake: Prospect vs Insured ── */
  const [prospectOrInsured, setProspectOrInsured] = useState<'Prospect' | 'Insured' | ''>('');
  const [intakeError, setIntakeError] = useState('');

  /* Prospect path */
  const [prospectMode, setProspectMode] = useState<'createNew' | 'useExisting' | ''>('');
  const prospectSearch = useClientSearch();
  const [selectedProspectClient, setSelectedProspectClient] = useState<NowCertsClient | null>(null);
  const [createQuoteForProspect, setCreateQuoteForProspect] = useState(true);

  /* Insured path */
  const [insuredMode, setInsuredMode] = useState<'createNew' | 'useExisting' | ''>('');
  const insuredSearch = useClientSearch();
  const [selectedInsuredClient, setSelectedInsuredClient] = useState<any>(null);
  const [createQuoteForInsured, setCreateQuoteForInsured] = useState(false);

  /* ── Upload ── */
  const [documentType, setDocumentType] = useState('');
  const [uploadTab, setUploadTab] = useState<'local' | 'ams'>('local');
  const [file, setFile] = useState<File | null>(null);
  const [selectedAmsFile, setSelectedAmsFile] = useState<AmsFileSelection | null>(null);
  /* When intake mode is "Create New" there's no client yet to browse files for —
   * the AMS tab needs its own client search so the user can pick whose AMS
   * folder to pull the source document from before getInsuredFilesList runs. */
  const amsClientSearch = useClientSearch();
  const [amsSelectedClient, setAmsSelectedClient] = useState<NowCertsClient | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── Review (extracted data) ── */
  const [insuredData, setInsuredData] = useState<InsuredData | null>(null);
  const [coverageLimitDetails, setCoverageLimitDetails] = useState<CoverageLimitDetails | null>(null);
  const [propertyDetails, setPropertyDetails] = useState<PropertyItem[]>([]);
  const [vehicleDetails, setVehicleDetails] = useState<VehicleItem[]>([]);
  const [driverDetails, setDriverDetails] = useState<DriverItem[]>([]);
  const [taxesAndFees, setTaxesAndFees] = useState<FeeItem[]>([]);
  const [activeReviewTab, setActiveReviewTab] = useState<'Insured' | 'Coverage' | 'Property' | 'Vehicle' | 'Driver' | 'Fees'>('Insured');
  const [isUpdateInNowCerts, setIsUpdateInNowCerts] = useState(false);

  const [allCarrierNames, setAllCarrierNames] = useState<string[]>([]);
  const [carrierNameLoading, setCarrierNameLoading] = useState(false);

  /* Fetches the full carrier dropdown list once (seeded with the extracted carrier
   * name) and auto-selects the matched carrier — mirrors getNCCarrierList() in the
   * legacy SmartScanLandingPage.tsx. */
  const getNCCarrierList = useCallback((seedCarrierName: string) => {
    setCarrierNameLoading(true);
    axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/NowCert/getnowcertcarrierlist`, null, {
      params: { carrierName: seedCarrierName, ncToken: getNcToken() },
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then((res) => {
        setAllCarrierNames(Array.isArray(res.data?.allCarrierNames) ? res.data.allCarrierNames : []);
        const matched = res.data?.matchedCarrierName;
        if (matched) setInsuredData((p) => (p ? { ...p, carrierName: matched } : p));
      })
      .catch(() => {})
      .finally(() => setCarrierNameLoading(false));
  }, []);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitResults, setSubmitResults] = useState<Array<{ label: string; success: boolean; message: string }>>([]);
  const [ncUrl, setNcUrl] = useState('');
  const [createdNewClient, setCreatedNewClient] = useState(false);

  /* Feedback popup (post-submit) */
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackAnchor, setFeedbackAnchor] = useState<{ bottom: number; right: number } | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<boolean | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* ── Client selection handlers ── */
  const selectProspectClient = useCallback((client: NowCertsClient) => {
    setSelectedProspectClient(client);
    prospectSearch.setQuery('');
    prospectSearch.setResults([]);
  }, [prospectSearch]);

  const selectInsuredClient = useCallback((client: NowCertsClient) => {
    setSelectedInsuredClient(client);
    insuredSearch.setQuery('');
    insuredSearch.setResults([]);
  }, [insuredSearch]);

  /* ── Intake validation (mirrors legacy checkValidationForProspect/Insured) ── */
  const validateIntake = (): boolean => {
    if (prospectOrInsured === '') {
      setIntakeError('Please select Prospect or Insured');
      return false;
    }
    if (prospectOrInsured === 'Prospect') {
      if (prospectMode === '') { setIntakeError('Please select a prospect client action'); return false; }
      if (prospectMode === 'useExisting' && !selectedProspectClient) { setIntakeError('Please select a prospect'); return false; }
      if (prospectMode === 'useExisting' && !createQuoteForProspect) { setIntakeError('Please select a prospect quote action'); return false; }
    } else {
      if (insuredMode === '') { setIntakeError('Please select an insured client action'); return false; }
      if (insuredMode === 'useExisting' && !selectedInsuredClient) { setIntakeError('Please select a client'); return false; }
    }
    setIntakeError('');
    return true;
  };

  const onNextIntake = () => {
    if (!validateIntake()) return;
    setStep('upload');
  };

  const onBackToIntake = () => setStep('intake');

  /* ── File handling ── */
  const pickFile = useCallback((incoming: FileList | File[]) => {
    const first = Array.from(incoming).find((f) => /\.pdf$/i.test(f.name));
    if (first) { setFile(first); setUploadError(''); }
    else { setUploadError('Please select PDF files only.'); setTimeout(() => setUploadError(''), 3000); }
  }, []);
  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); pickFile(e.dataTransfer.files); };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.files) pickFile(e.target.files); e.target.value = ''; };

  const canScan = !uploadLoading && !!documentType &&
    (uploadTab === 'local' ? !!file : !!selectedAmsFile);

  /* ── Build SelectedOption + start scan ── */
  const buildSelectedOption = (folderId: string, fileIds: string[], isAms: boolean): SelectedOption => ({
    SelectProspect: prospectOrInsured === 'Prospect',
    SelectInsured: prospectOrInsured === 'Insured',
    CreateNewClient: (prospectOrInsured === 'Prospect' && prospectMode === 'createNew') || (prospectOrInsured === 'Insured' && insuredMode === 'createNew'),
    UseExistingInsured: prospectOrInsured === 'Insured' && insuredMode === 'useExisting',
    UseExistingProspect: prospectOrInsured === 'Prospect' && prospectMode === 'useExisting',
    CreateQuote: prospectOrInsured === 'Prospect' ? createQuoteForProspect : createQuoteForInsured,
    CreateNewPolicy: prospectOrInsured === 'Insured',
    CreateNewProspect: prospectOrInsured === 'Prospect' && prospectMode === 'createNew',
    ClientName: prospectOrInsured === 'Prospect' ? (selectedProspectClient?.name || '') : (selectedInsuredClient?.name || ''),
    InsuredId: prospectOrInsured === 'Insured'
      ? (selectedInsuredClient?.databaseId || amsSelectedClient?.databaseId || '')
      : (selectedProspectClient?.databaseId || amsSelectedClient?.databaseId || ''),
    folderId, fileIds, isFileUploadFromAMSParam: isAms,
  });

  const onStartScan = useCallback(async () => {
    if (!documentType) { setUploadError('Please select document type.'); return; }
    const isAms = uploadTab === 'ams';
    if (!isAms && !file) return;
    if (isAms && !selectedAmsFile) return;

    setUploadLoading(true);
    setUploadError('');
    try {
      const folderId = isAms ? (selectedAmsFile?.folderId || '') : '';
      const fileIds = isAms ? [selectedAmsFile?.file.id || ''] : [];
      const insuredIdForRequest = prospectOrInsured === 'Insured'
        ? (selectedInsuredClient?.databaseId || amsSelectedClient?.databaseId || '')
        : (selectedProspectClient?.databaseId || amsSelectedClient?.databaseId || '');

      const fileName = isAms ? (selectedAmsFile?.file.name || '') : (file?.name || '');

      const data = new FormData();
      if (!isAms && file) data.append('File', file);
      data.append('documentType', documentType);
      data.append('ncToken', getNcToken());
      fileIds.forEach((id) => data.append('amsFileIds', id));
      data.append('folderId', folderId);
      data.append('insuredId', insuredIdForRequest);
      data.append('isFileUploadFromAMS', String(isAms));
      data.append('fileName', fileName);
      data.append('selectedOption', JSON.stringify(buildSelectedOption(folderId, fileIds, isAms)));
      const res = await axios.post(`${process.env.REACT_APP_AcordScan_Url}/accord_scan`, data, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });

      if (res.data.success === true) {
        const raw = isDecOrQuote(documentType) && typeof res.data.acordFormResponse === 'string'
          ? JSON.parse(res.data.acordFormResponse)
          : (typeof res.data.acordFormResponse === 'string' ? JSON.parse(res.data.acordFormResponse) : (res.data.acordFormResponse || res.data));

        const lob = Array.isArray(raw.linesOfBusiness) ? (raw.linesOfBusiness[0] || '') : (raw.linesOfBusiness || raw.lineOfBusiness || '');
        const extracted: InsuredData = {
          businessName: raw.businessName || '',
          firstName: raw.firstName || '',
          lastName: raw.lastName || '',
          email: raw.email || '',
          phoneNumber: raw.phoneNumber || '',
          policyNumber: raw.policyNumber || '',
          policyPremium: raw.policyPremium || '',
          carrierName: raw.carrierName || '',
          lineOfBusiness: lob,
          quoteNumber: raw.quoteNumber || '',
          insuredType: raw.insuredType || (raw.businessName ? 'Commercial' : 'Personal'),
          fees: raw.fees || '',
          taxes: raw.taxes || '',
          effectiveDate: raw.effectiveDate || '',
          expirationDate: raw.expirationDate || '',
          address: raw.address || '',
          city: raw.city || '',
          state: raw.state || '',
          zipCode: raw.zipCode || '',
          naicsCode: raw.naicsCode || '',
          submissionId: raw.submissionId || res.data.submissionId || '',
          fileUrl: raw.fileUrl || res.data.fileUrl || '',
        };
        setInsuredData(extracted);
        getNCCarrierList(extracted.carrierName);

        if (isDecOrQuote(documentType)) {
          setCoverageLimitDetails(raw.coverageLimitDetails || null);
          setPropertyDetails(Array.isArray(raw.propertyDetails) ? raw.propertyDetails : (raw.propertyDetails ? [raw.propertyDetails] : []));
          setVehicleDetails(Array.isArray(raw.vehicleDetails) ? raw.vehicleDetails : []);
          setDriverDetails(Array.isArray(raw.driverDetails) ? raw.driverDetails : []);
        } else {
          setCoverageLimitDetails(null);
          setPropertyDetails([]);
          setVehicleDetails([]);
          setDriverDetails([]);
        }
        setTaxesAndFees(Array.isArray(raw.taxesAndFees) ? raw.taxesAndFees : []);
        setActiveReviewTab('Insured');
        setStep('review');
      } else {
        setUploadError(res.data.errorMessage || 'Error while creating the request.');
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        navigate(ROUTES.LOGIN);
      } else {
        setUploadError(err.response?.data?.errorMessage || 'Error while creating the request.');
      }
    } finally {
      setUploadLoading(false);
    }
  }, [documentType, uploadTab, file, selectedAmsFile, prospectOrInsured, selectedInsuredClient, selectedProspectClient, amsSelectedClient, navigate, getNCCarrierList]);

  const resetAll = useCallback(() => {
    setStep('intake');
    setProspectOrInsured('');
    setIntakeError('');
    setProspectMode(''); setSelectedProspectClient(null); setCreateQuoteForProspect(true);
    setInsuredMode(''); setSelectedInsuredClient(null); setCreateQuoteForInsured(false);
    setDocumentType(''); setUploadTab('local'); setFile(null); setSelectedAmsFile(null);
    setAmsSelectedClient(null); amsClientSearch.setQuery(''); amsClientSearch.setResults([]);
    setUploadError('');
    setInsuredData(null); setCoverageLimitDetails(null);
    setPropertyDetails([]); setVehicleDetails([]); setDriverDetails([]); setTaxesAndFees([]);
    setIsUpdateInNowCerts(false); setSubmitResults([]);
    setNcUrl(''); setCreatedNewClient(false);
    setFeedbackOpen(false); setFeedbackAnchor(null); setFeedbackText(''); setFeedbackRating(null); setFeedbackError('');
  }, []);

  /* ── Feedback (post-submit) — same anchored-popup pattern as History's feedback popup ── */
  const openFeedback = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedbackError('');
    /* Anchored above the button (via `bottom`, not `top`) since this button sits
     * near the bottom of the viewport — anchoring below would push the popup
     * off-screen. */
    setFeedbackAnchor({ bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right });
    setFeedbackOpen(true);
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedbackError('');
  }, []);

  const sendFeedback = useCallback((isLiked: boolean | null, text: string) => {
    const submissionId = insuredData?.submissionId || '';
    if (!submissionId) return;
    axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCAICommissionStatement/feedback`,
      { TransactionId: submissionId, IsLiked: isLiked, Feedback: text, Target: 3 },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => showSuccess('Feedback submitted successfully!'))
      .catch(() => {});
  }, [insuredData]);

  /* Thumbs up/down submit immediately (with whatever text has been typed so far)
   * and close the popup — same as History's onThumbsUpClick/onThumbsDownClick. */
  const onThumbsUpClick = useCallback(() => {
    const next = feedbackRating === true ? null : true;
    sendFeedback(next, feedbackText);
    closeFeedback();
  }, [feedbackRating, feedbackText, sendFeedback, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    const next = feedbackRating === false ? null : false;
    sendFeedback(next, feedbackText);
    closeFeedback();
  }, [feedbackRating, feedbackText, sendFeedback, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackText.trim()) { setFeedbackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Please provide your feedback within 250 character'); return; }
    setFeedbackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCAICommissionStatement/feedback`,
      { TransactionId: insuredData?.submissionId || '', IsLiked: feedbackRating, Feedback: feedbackText, Target: 3 },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => setFeedbackSubmitting(false));
  }, [feedbackText, feedbackRating, insuredData, closeFeedback]);

  useEffect(() => {
    if (!feedbackOpen) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackOpen, closeFeedback]);

  /* ── Insured Details field updates ── */
  const updateInsured = (patch: Partial<InsuredData>) => setInsuredData((p) => (p ? { ...p, ...patch } : p));

  /* ── Fees & Taxes row updates ── */
  const updateFeeRow = (index: number, patch: Partial<FeeItem>) =>
    setTaxesAndFees((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  /* ── Submit: push extracted data into NowCerts ── */
  const validateInsuredForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!insuredData) return false;
    if (insuredData.insuredType !== 'Personal' && !insuredData.businessName.trim()) errors.businessName = 'Business name is required';
    if (insuredData.insuredType === 'Personal' && (!insuredData.firstName.trim() || !insuredData.lastName.trim())) errors.firstName = 'First and last name are required';
    const willCreatePolicy = prospectOrInsured === 'Insured';
    if (willCreatePolicy && !insuredData.policyNumber.trim()) errors.policyNumber = 'Policy number is required';
    const willCreateQuote = prospectOrInsured === 'Prospect' ? createQuoteForProspect : createQuoteForInsured;
    if (willCreateQuote && !insuredData.quoteNumber.trim()) errors.quoteNumber = 'Quote number is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmitClick = () => {
    if (!validateInsuredForm()) return;
    setShowSubmitConfirm(true);
  };

  const onSubmitConfirmed = useCallback(async () => {
    if (!insuredData) return;
    setShowSubmitConfirm(false);
    setSubmitLoading(true);
    setSubmitResults([]);
    try {
      const isProspect = prospectOrInsured === 'Prospect';
      const createNewClient = (isProspect && prospectMode === 'createNew') || (!isProspect && insuredMode === 'createNew');
      const insuredId = isProspect ? (selectedProspectClient?.databaseId || '') : (selectedInsuredClient?.databaseId || '');

      const { ncUrl: newNcUrl, createdNewClient: newClientCreated } = await submitAcordSmartScanToNowCerts({
        insured: insuredData,
        coverageLimitDetails,
        propertyDetails,
        vehicleDetails,
        driverDetails,
        taxesAndFees,
        documentType,
        submissionId: insuredData.submissionId || '',
        isProspect,
        createNewClient,
        createQuote: isProspect ? createQuoteForProspect : createQuoteForInsured,
        insuredId,
      }, (step) => setSubmitResults((prev) => [...prev, step]));

      setNcUrl(newNcUrl);
      setCreatedNewClient(newClientCreated);
      setIsUpdateInNowCerts(true);
    } finally {
      setSubmitLoading(false);
    }
  }, [insuredData, prospectOrInsured, prospectMode, insuredMode, selectedInsuredClient, selectedProspectClient,
    createQuoteForProspect, createQuoteForInsured, propertyDetails, vehicleDetails, driverDetails,
    taxesAndFees, coverageLimitDetails, documentType]);

  const insuredIdForProperty = prospectOrInsured === 'Insured' ? (selectedInsuredClient?.databaseId || '') : (selectedProspectClient?.databaseId || '');

  /* AMS file browser needs a client to browse: the existing client picked in
   * intake ("Use Existing"), or — when intake is "Create New" — whichever
   * client the user searches for and picks specifically for file browsing. */
  const amsIntakeMode = prospectOrInsured === 'Insured' ? insuredMode : prospectMode;
  const amsExistingClient = prospectOrInsured === 'Insured' ? selectedInsuredClient : selectedProspectClient;
  const amsBrowseClient = amsIntakeMode === 'useExisting' ? amsExistingClient : amsSelectedClient;

  /* ════════════════════ RENDER: STEP 1 — INTAKE ════════════════════ */
  if (step === 'intake') {
    return (
      <div className="cs-main acord-smartscan-upload__main-flat">
        <div className="acord-smartscan-upload__radio-row">
          {(['Prospect', 'Insured'] as const).map((opt) => (
            <RadioOption
              key={opt}
              checked={prospectOrInsured === opt}
              onClick={() => { setProspectOrInsured(opt); setIntakeError(''); }}
              label={`Select ${opt}`}
            />
          ))}
        </div>

        {prospectOrInsured === 'Prospect' && (
          <div className="acord-smartscan-upload__intake-col">
            <div>
              <h4 className="acord-smartscan-upload__section-title">Prospect</h4>
              <div className="acord-smartscan-upload__field-col-10">
                <ToggleSwitch
                  checked={prospectMode === 'createNew'}
                  onChange={(v) => { setProspectMode(v ? 'createNew' : ''); if (v) setSelectedProspectClient(null); }}
                  label="Create New"
                />
                <ToggleSwitch
                  checked={prospectMode === 'useExisting'}
                  onChange={(v) => setProspectMode(v ? 'useExisting' : '')}
                  label="Use Existing"
                />
              </div>
              {prospectMode === 'useExisting' && (
                <div className="cs-client-search acord-smartscan-upload__search-mt">
                  <div className="cs-client-search__input-wrap">
                    <FiSearch size={15} className="cs-client-search__icon" />
                    <input
                      className="cs-client-search__input"
                      placeholder="Search prospect name…"
                      value={selectedProspectClient ? selectedProspectClient.name : prospectSearch.query}
                      onChange={(e) => { setSelectedProspectClient(null); prospectSearch.setQuery(e.target.value); }}
                    />
                    {prospectSearch.loading && <span className="cs-client-search__spinner" />}
                  </div>
                  {prospectSearch.results.length > 0 && !selectedProspectClient && (
                    <ul className="cs-client-dropdown">
                      {prospectSearch.results.map((c) => (
                        <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => selectProspectClient(c)}>
                          <span className="cs-client-dropdown__name">{c.name}</span>
                          {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div>
              <h4 className="acord-smartscan-upload__section-title">Quote</h4>
              <ToggleSwitch checked={createQuoteForProspect} onChange={setCreateQuoteForProspect} label="Create Quote" />
            </div>
          </div>
        )}

        {prospectOrInsured === 'Insured' && (
          <div className="acord-smartscan-upload__intake-col">
            <div>
              <h4 className="acord-smartscan-upload__section-title">Client</h4>
              <div className="acord-smartscan-upload__field-col-10">
                <ToggleSwitch
                  checked={insuredMode === 'createNew'}
                  onChange={(v) => { setInsuredMode(v ? 'createNew' : ''); if (v) setSelectedInsuredClient(null); }}
                  label="Create New"
                />
                <ToggleSwitch
                  checked={insuredMode === 'useExisting'}
                  onChange={(v) => setInsuredMode(v ? 'useExisting' : '')}
                  label="Use Existing"
                />
              </div>
              {insuredMode === 'useExisting' && (
                <div className="cs-client-search acord-smartscan-upload__search-mt">
                  <div className="cs-client-search__input-wrap">
                    <FiSearch size={15} className="cs-client-search__icon" />
                    <input
                      className="cs-client-search__input"
                      placeholder="Search client name…"
                      value={selectedInsuredClient ? selectedInsuredClient.name : insuredSearch.query}
                      onChange={(e) => { setSelectedInsuredClient(null); insuredSearch.setQuery(e.target.value); }}
                    />
                    {insuredSearch.loading && <span className="cs-client-search__spinner" />}
                  </div>
                  {insuredSearch.results.length > 0 && !selectedInsuredClient && (
                    <ul className="cs-client-dropdown">
                      {insuredSearch.results.map((c) => (
                        <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => selectInsuredClient(c)}>
                          <span className="cs-client-dropdown__name">{c.name}</span>
                          {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div>
              <h4 className="acord-smartscan-upload__section-title">Quote</h4>
              <ToggleSwitch checked={createQuoteForInsured} onChange={setCreateQuoteForInsured} label="Create Quote" />
            </div>
            <div>
              <h4 className="acord-smartscan-upload__section-title">Policy</h4>
              <ToggleSwitch checked onChange={() => {}} label="Create Policy" />
            </div>
          </div>
        )}

        {intakeError && <div className="acord-smartscan-upload__error-text">{intakeError}</div>}

        {prospectOrInsured && (
          <div className="acord-smartscan-upload__actions-end">
            <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={onNextIntake}>
              Next →
            </motion.button>
          </div>
        )}
      </div>
    );
  }

  /* ════════════════════ RENDER: STEP 2 — UPLOAD ════════════════════ */
  if (step === 'upload') {
    const intakeSummary: Array<{ label: string; value: string }> = [];
    if (prospectOrInsured === 'Prospect') {
      intakeSummary.push({ label: 'Prospect', value: prospectMode === 'useExisting' ? (selectedProspectClient?.name || '—') : 'Create Prospect' });
      intakeSummary.push({ label: 'Quote', value: createQuoteForProspect ? 'Create Quote' : '—' });
    } else if (prospectOrInsured === 'Insured') {
      intakeSummary.push({ label: 'Insured', value: insuredMode === 'useExisting' ? (selectedInsuredClient?.name || '—') : 'Create Insured' });
      intakeSummary.push({ label: 'Quote', value: createQuoteForInsured ? 'Create Quote' : '—' });
      intakeSummary.push({ label: 'Policy', value: 'Create Policy' });
    }

    return (
      <div className="cs-main acord-smartscan-upload__main-flat">
        <div className="acord-smartscan-upload__row-wrap-20">
          <button className="cs-btn-cancel acord-smartscan-upload__btn-inline" onClick={onBackToIntake}>
            <FiChevronLeft size={13} /> Back
          </button>
          {intakeSummary.map((it, i) => (
            <span key={i} className="acord-smartscan-upload__summary-item">
              <strong className="acord-smartscan-upload__summary-label">{it.label}:</strong>{' '}
              <span className="acord-smartscan-upload__summary-value">{it.value}</span>
            </span>
          ))}
        </div>

        <div className="cs-tabs">
          <button className={`cs-tab${uploadTab === 'local' ? ' cs-tab--active' : ''}`} onClick={() => { setUploadTab('local'); setSelectedAmsFile(null); }}>
            Select Document from Local Drive
          </button>
          <button className={`cs-tab${uploadTab === 'ams' ? ' cs-tab--active' : ''}`} onClick={() => { setUploadTab('ams'); setFile(null); }}>
            Select Document from Client Profile
          </button>
        </div>

        <div className="cs-content">
          {uploadTab === 'local' ? (
            <div
              className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
            >
              <div className="ho-form-field acord-smartscan-upload__doc-type-field">
                <select className="ho-select" value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
                  <option value="">Select Document Type</option>
                  {DOCUMENT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {!file ? (
                <>
                  <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
                  <p className="cs-dropzone__title">Drag & Drop your document here</p>
                  <p className="cs-dropzone__or">or</p>
                  <motion.button className="cs-browse-btn" onClick={() => fileInputRef.current?.click()} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    Browse Files
                  </motion.button>
                  <p className="cs-dropzone__types">Supported document types: Acord 125, Acord 126, Acord 130, Dec Page and Quote Letter. Allowed file format: PDF.</p>
                </>
              ) : (
                <div className="cs-file-card">
                  <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                  <div className="cs-file-card__info">
                    <span className="cs-file-card__name">{file.name}</span>
                    <span className="cs-file-card__size">{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button className="cs-file-card__delete" onClick={() => setFile(null)} title="Remove">
                    <FiTrash2 size={15} />
                  </button>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept=".pdf" className="acord-smartscan-upload__hidden-input" onChange={handleFileInput} />
            </div>
          ) : (
            <>
              <div className="acord-smartscan-upload__ams-search-row">
                <div className="cs-client-search acord-smartscan-upload__search-flex">
                  <div className="cs-client-search__input-wrap">
                    <FiSearch size={15} className="cs-client-search__icon" />
                    <input
                      className="cs-client-search__input"
                      placeholder="Enter client name"
                      readOnly={amsIntakeMode === 'useExisting'}
                      value={amsIntakeMode === 'useExisting' ? (amsExistingClient?.name || '') : (amsSelectedClient ? amsSelectedClient.name : amsClientSearch.query)}
                      onChange={(e) => {
                        if (amsIntakeMode === 'useExisting') return;
                        setAmsSelectedClient(null);
                        setSelectedAmsFile(null);
                        amsClientSearch.setQuery(e.target.value);
                      }}
                    />
                    {amsClientSearch.loading && <span className="cs-client-search__spinner" />}
                  </div>
                  {amsClientSearch.results.length > 0 && !amsSelectedClient && amsIntakeMode !== 'useExisting' && (
                    <ul className="cs-client-dropdown">
                      {amsClientSearch.results.map((c) => (
                        <li
                          key={c.databaseId}
                          className="cs-client-dropdown__item"
                          onClick={() => {
                            setAmsSelectedClient(c);
                            amsClientSearch.setQuery('');
                            amsClientSearch.setResults([]);
                            setSelectedAmsFile(null);
                          }}
                        >
                          <span className="cs-client-dropdown__name">{c.name}</span>
                          {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="ho-form-field acord-smartscan-upload__doc-type-field-flex">
                  <select className="ho-select" value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
                    <option value="">Select Document Type</option>
                    {DOCUMENT_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {amsBrowseClient && (
                <AmsFileBrowser
                  insuredId={amsBrowseClient.databaseId}
                  clientName={amsBrowseClient.name}
                  onFileSelected={setSelectedAmsFile}
                />
              )}
            </>
          )}
        </div>

        <div className="cs-info-banner">
          <FiAlertCircle size={14} />
          <span>This tool works best with readable, text-based PDFs — scanned/image PDFs may not process correctly.</span>
        </div>

        {uploadError && <div className="acord-smartscan-upload__error-text-padded">{uploadError}</div>}

        <div className="cs-footer">
          <span className="cs-footer__status">
            {uploadTab === 'local'
              ? (!file ? 'No file added yet.' : `${file.name} ready to scan`)
              : (!selectedAmsFile ? 'No file selected.' : `${selectedAmsFile.file.name} ready to scan`)}
          </span>
          <div className="cs-footer__actions">
            <button className="cs-btn-cancel" onClick={resetAll} disabled={uploadLoading}>Cancel</button>
            <motion.button className="cs-btn-start" disabled={!canScan} whileHover={{ scale: canScan ? 1.03 : 1 }} whileTap={{ scale: canScan ? 0.97 : 1 }} onClick={onStartScan}>
              {uploadLoading ? <span className="cs-upload-spinner" /> : '✦ Start Scan'}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════ RENDER: STEP 3 — REVIEW ════════════════════ */
  if (!insuredData) return null;
  const isReadOnly = isUpdateInNowCerts;

  const optionStyle = getOptionStyle(theme);

  const ctrlStyle = (editable: boolean): React.CSSProperties => ({
    width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 12,
    background: editable ? 'var(--surface-glass)' : 'rgba(255,255,255,0.03)',
    border: `1px solid ${editable ? 'var(--border-glass)' : 'rgba(255,255,255,0.06)'}`,
    color: editable ? 'var(--text-primary)' : 'var(--text-secondary)',
    outline: 'none', boxSizing: 'border-box' as const, cursor: editable ? 'text' : 'default',
  });

  const fieldWrap = (label: string, node: React.ReactNode, error?: string) => (
    <div>
      <div className="acord-smartscan-upload__field-label">{label}</div>
      {node}
      {error && <span className="acord-smartscan-upload__field-error">{error}</span>}
    </div>
  );

  const REVIEW_TABS: Array<{ key: typeof activeReviewTab; label: string; show: boolean }> = [
    { key: 'Insured', label: 'Insured Details', show: true },
    { key: 'Coverage', label: 'Coverage Limit Details', show: isDecOrQuote(documentType) },
    { key: 'Property', label: 'Property Details', show: isDecOrQuote(documentType) },
    { key: 'Vehicle', label: 'Vehicle Details', show: isDecOrQuote(documentType) },
    { key: 'Driver', label: 'Driver Details', show: isDecOrQuote(documentType) },
    { key: 'Fees', label: 'Fees and Taxes', show: taxesAndFees.length > 0 },
  ];

  const reviewIntakeSummary: Array<{ label: string; value: string }> = [];
  if (prospectOrInsured === 'Prospect') {
    reviewIntakeSummary.push({ label: 'Prospect', value: prospectMode === 'useExisting' ? (selectedProspectClient?.name || '—') : 'Create Prospect' });
    reviewIntakeSummary.push({ label: 'Quote', value: createQuoteForProspect ? 'Create Quote' : '—' });
  } else if (prospectOrInsured === 'Insured') {
    reviewIntakeSummary.push({ label: 'Insured', value: insuredMode === 'useExisting' ? (selectedInsuredClient?.name || '—') : 'Create Insured' });
    reviewIntakeSummary.push({ label: 'Quote', value: createQuoteForInsured ? 'Create Quote' : '—' });
    reviewIntakeSummary.push({ label: 'Policy', value: 'Create Policy' });
  }

  return (
    <>
    <div className="cs-main acord-smartscan-upload__main-flat">
      <div className="acord-smartscan-upload__header-row-between">
        <div className="acord-smartscan-upload__row-wrap-20">
          <button className="cs-btn-cancel acord-smartscan-upload__btn-inline" onClick={() => setStep('upload')}>
            <FiChevronLeft size={13} /> Back
          </button>
          {reviewIntakeSummary.map((it, i) => (
            <span key={i} className="acord-smartscan-upload__summary-item">
              <strong className="acord-smartscan-upload__summary-label">{it.label}:</strong>{' '}
              <span className="acord-smartscan-upload__summary-value">{it.value}</span>
            </span>
          ))}
        </div>
        {isUpdateInNowCerts && (
          <span className="acord-smartscan-upload__updated-badge">
            ✓ Updated in AMS
          </span>
        )}
      </div>

      <div className="acord-smartscan-upload__review-body-row">
        {/* Left: scanned document */}
        <div className="cspr-pdf-panel acord-smartscan-upload__pdf-panel-box">
          <div className="cspr-pdf-panel__header">
            <span className="cspr-pdf-panel__title">Scanned Document</span>
          </div>
          <div className="cspr-pdf-viewer">
            {insuredData.fileUrl ? (
              <iframe src={`${insuredData.fileUrl}#zoom=90`} title="Scanned Document" className="acord-smartscan-upload__pdf-iframe" />
            ) : (
              <div className="cspr-pdf-placeholder">Preview not available</div>
            )}
          </div>
        </div>

        {/* Right: extracted data */}
        <div className="acord-smartscan-upload__review-data-col">
          <div className="cs-tabs acord-smartscan-upload__tabs-wrap">
            {REVIEW_TABS.filter((t) => t.show).map((t) => (
              <button key={t.key} className={`cs-tab${activeReviewTab === t.key ? ' cs-tab--active' : ''}`} onClick={() => setActiveReviewTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          {activeReviewTab === 'Insured' && (
        <div className="acord-smartscan-upload__review-col">
          <div className="acord-smartscan-upload__insured-grid">
            {insuredData.insuredType !== 'Personal' ? (
              fieldWrap('Business Name', (
                <input value={insuredData.businessName} readOnly={isReadOnly} onChange={(e) => updateInsured({ businessName: e.target.value })} style={ctrlStyle(!isReadOnly)} />
              ), fieldErrors.businessName)
            ) : (
              <>
                {fieldWrap('First Name', (
                  <input value={insuredData.firstName} readOnly={isReadOnly} onChange={(e) => updateInsured({ firstName: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ), fieldErrors.firstName)}
                {fieldWrap('Last Name', (
                  <input value={insuredData.lastName} readOnly={isReadOnly} onChange={(e) => updateInsured({ lastName: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ))}
              </>
            )}
            {fieldWrap('Email', (
              <input value={insuredData.email} readOnly={isReadOnly} onChange={(e) => updateInsured({ email: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ))}
            {fieldWrap('Phone Number', (
              <input value={insuredData.phoneNumber} readOnly={isReadOnly} onChange={(e) => updateInsured({ phoneNumber: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ))}
            {fieldWrap('Policy Number', (
              <input value={insuredData.policyNumber} readOnly={isReadOnly} onChange={(e) => updateInsured({ policyNumber: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ), fieldErrors.policyNumber)}
            {fieldWrap('Policy Premium ($)', (
              <input value={insuredData.policyPremium} readOnly={isReadOnly} onChange={(e) => updateInsured({ policyPremium: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ))}
            {fieldWrap('Carrier Name', (
              carrierNameLoading ? (
                <div className="acord-smartscan-upload__carrier-loading-row">
                  <span className="cs-client-search__spinner" />
                </div>
              ) : (
                <select
                  value={insuredData.carrierName}
                  disabled={isReadOnly}
                  onChange={(e) => updateInsured({ carrierName: e.target.value })}
                  style={ctrlStyle(!isReadOnly)}
                >
                  <option value="" style={optionStyle}>Select an option</option>
                  {allCarrierNames.map((name) => (
                    <option key={name} value={name} title={name} style={optionStyle}>
                      {name.length > 32 ? `${name.substring(0, 32)}...` : name}
                    </option>
                  ))}
                </select>
              )
            ))}
            {fieldWrap('Insured Type', (
              <select value={insuredData.insuredType} disabled={isReadOnly} onChange={(e) => updateInsured({ insuredType: e.target.value })} style={ctrlStyle(!isReadOnly)}>
                <option value="Commercial" style={optionStyle}>Commercial</option>
                <option value="Personal" style={optionStyle}>Personal</option>
              </select>
            ))}
            {fieldWrap('Line of Business', (
              <input value={insuredData.lineOfBusiness} readOnly={isReadOnly} onChange={(e) => updateInsured({ lineOfBusiness: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ))}
            {fieldWrap('Quote Number', (
              <input value={insuredData.quoteNumber} readOnly={isReadOnly} onChange={(e) => updateInsured({ quoteNumber: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ), fieldErrors.quoteNumber)}
            {fieldWrap('Effective Date', (
              <input type="date" value={toDateInput(insuredData.effectiveDate)} readOnly={isReadOnly} onChange={(e) => updateInsured({ effectiveDate: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ))}
            {fieldWrap('Expiration Date', (
              <input type="date" value={toDateInput(insuredData.expirationDate)} readOnly={isReadOnly} onChange={(e) => updateInsured({ expirationDate: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ))}
            {fieldWrap('Address', (
              <input value={insuredData.address} readOnly={isReadOnly} onChange={(e) => updateInsured({ address: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ))}
            {fieldWrap('City', (
              <input value={insuredData.city} readOnly={isReadOnly} onChange={(e) => updateInsured({ city: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ))}
            {fieldWrap('State', (
              <select value={insuredData.state} disabled={isReadOnly} onChange={(e) => updateInsured({ state: e.target.value })} style={ctrlStyle(!isReadOnly)}>
                <option value="" style={optionStyle}>Select State</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code} style={optionStyle}>{s.name}</option>
                ))}
              </select>
            ))}
            {fieldWrap('Zip Code', (
              <input value={insuredData.zipCode} readOnly={isReadOnly} onChange={(e) => updateInsured({ zipCode: e.target.value })} style={ctrlStyle(!isReadOnly)} />
            ))}
          </div>
        </div>
      )}

      {activeReviewTab === 'Coverage' && (
        <CoverageLimitCard lineOfBusiness={insuredData.lineOfBusiness} coverageLimitDetails={coverageLimitDetails} />
      )}

      {activeReviewTab === 'Property' && (
        <div className="acord-smartscan-upload__property-col">
          <SortablePaginatedTable
            title="Property Details"
            rows={propertyDetails.map((p) => ({
              'Address Line 1': p.AddressLine1 || '', City: p.City || '', State: p.State || '',
              'Zip Code': p.ZipCode || '', 'Property Use': p.PropertyUseCode || '',
            }))}
            columns={[
              { key: 'Address Line 1', label: 'Address Line 1' },
              { key: 'City', label: 'City' },
              { key: 'State', label: 'State' },
              { key: 'Zip Code', label: 'Zip Code' },
              { key: 'Property Use', label: 'Property Use' },
            ]}
            emptyLabel="No property details found."
          />
          <p className="acord-smartscan-upload__property-note">
            Note: For adding Acord 80 to property, please add property in AMS first and then try to add Acord 80 In AMS.
          </p>
        </div>
      )}

      {activeReviewTab === 'Vehicle' && (
        <SortablePaginatedTable
          title="Vehicle Details"
          rows={vehicleDetails.map((v) => ({ Make: v.Make || '', Model: v.Model || '', Year: v.Year || '', VIN: v.Vin || '' }))}
          columns={[
            { key: 'Make', label: 'Make' },
            { key: 'Model', label: 'Model' },
            { key: 'Year', label: 'Year' },
            { key: 'VIN', label: 'VIN' },
          ]}
          emptyLabel="No vehicle details found."
        />
      )}

      {activeReviewTab === 'Driver' && (
        <SortablePaginatedTable
          title="Driver Details"
          rows={driverDetails.map((d) => ({
            'First Name': d.FirstName || '', 'Last Name': d.LastName || '',
            'Date Of Birth': d.DateOfBirth || '', 'License Number': d.LicenseNumber || '',
          }))}
          columns={[
            { key: 'First Name', label: 'First Name' },
            { key: 'Last Name', label: 'Last Name' },
            { key: 'Date Of Birth', label: 'Date Of Birth' },
            { key: 'License Number', label: 'License Number' },
          ]}
          emptyLabel="No driver details found."
        />
      )}

      {activeReviewTab === 'Fees' && (
        <div className="acord-smartscan-upload__fees-table-wrap">
          <table className="acord-smartscan-upload__fees-table">
            <thead>
              <tr className="acord-smartscan-upload__fees-tr">
                <th className="acord-smartscan-upload__fees-th">Description</th>
                <th className="acord-smartscan-upload__fees-th">Amount</th>
                <th className="acord-smartscan-upload__fees-th">Mapping In AMS</th>
              </tr>
            </thead>
            <tbody>
              {taxesAndFees.map((f, i) => (
                <tr key={i} className="acord-smartscan-upload__fees-tr">
                  <td className="acord-smartscan-upload__fees-td">{f.descriptionField || '—'}</td>
                  <td className="acord-smartscan-upload__fees-td">{f.descriptionAmount || '—'}</td>
                  <td className="acord-smartscan-upload__fees-td-plain">
                    <select
                      value={f.mappingBillingTypeValue ?? ''}
                      disabled={isReadOnly}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        const bt = findBillingTypeByValue(value);
                        updateFeeRow(i, { mappingBillingType: bt?.name ?? '', mappingBillingTypeValue: value });
                      }}
                      className="acord-smartscan-upload__fees-select"
                    >
                      <option value="" style={optionStyle}>Select fee / tax type</option>
                      {BILLING_TYPE_DATA.map((bt) => (
                        <option key={bt.value} value={bt.value} style={optionStyle}>{bt.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Submit — shown on every tab, matching the legacy isSubmitVisible() simplification
       * (submit is no longer gated to a specific tab or per-tab data presence). */}
      {submitResults.length > 0 && (
        <div className="acord-smartscan-upload__submit-results-box">
          {submitResults.map((r, i) => (
            <div key={i} className="acord-smartscan-upload__submit-result-row" style={{ color: r.success ? '#10b981' : '#ef4444' }}>
              {r.success ? <FiCheckCircle size={13} /> : <FiAlertCircle size={13} />}
              <strong>{r.label}:</strong> {r.message}
            </div>
          ))}
        </div>
      )}

      {!isReadOnly && (
        <div className="acord-smartscan-upload__actions-end-gap">
          <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} disabled={submitLoading} onClick={onSubmitClick}>
            {submitLoading ? 'Submitting…' : 'Submit'}
          </motion.button>
        </div>
      )}

      {isReadOnly && (
        <div className="acord-smartscan-upload__actions-end-gap-wrap">
          {ncUrl && createdNewClient && (
            <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => window.open(ncUrl, '_blank')}>
              {prospectOrInsured === 'Prospect' ? 'Open Prospect' : 'Open Insured'}
            </motion.button>
          )}
          <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={resetAll}>
            Upload Again
          </motion.button>
          <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={openFeedback}>
            Feedback
          </motion.button>
        </div>
      )}

      {feedbackOpen && feedbackAnchor && (
        <div ref={feedbackRef} className="csfb-popup" style={{ bottom: feedbackAnchor.bottom, right: feedbackAnchor.right }}>
          <h4 className="csfb-title">Feedback</h4>
          <textarea className="csfb-textarea" placeholder="Enter your feedback here" value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={4} />
          <div className="csfb-rating">
            <button className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`} onClick={onThumbsUpClick} title="Thumbs up"><FiThumbsUp size={20} /></button>
            <button className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`} onClick={onThumbsDownClick} title="Thumbs down"><FiThumbsDown size={20} /></button>
          </div>
          {feedbackError && <p className="csfb-error">{feedbackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedbackClick}>
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {showSubmitConfirm && ReactDOM.createPortal(
        <div className="csams-overlay" onClick={() => setShowSubmitConfirm(false)}>
          <div className="csams-modal" onClick={(e) => e.stopPropagation()}>
            <div className="csams-modal__header">
              <h3 className="csams-modal__title">Confirm Submit</h3>
              <button className="csams-modal__close" onClick={() => setShowSubmitConfirm(false)}>✕</button>
            </div>
            <div className="csams-modal__body">
              <p className="acord-smartscan-upload__confirm-text">
                By clicking <span className="acord-smartscan-upload__confirm-highlight">Submit</span>, a new client/quote will be created in NowCerts. Are you sure you want to proceed?
              </p>
            </div>
            <div className="csams-modal__footer">
              <button className="csams-btn csams-btn--cancel" onClick={() => setShowSubmitConfirm(false)}>Cancel</button>
              <button className="csams-btn csams-btn--upload" onClick={onSubmitConfirmed}>Confirm</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
        </div>
      </div>
    </div>
    </>
  );
};

export default AcordSmartScanUpload;
